import {useMetrics, useReports} from '@sail/observability';
import {getEnvironment} from '@sail/utils';
import {useCallback} from 'react';
import {useNavigationConfig} from 'src/internal/config/NavigationConfigContext';
import routeToUrl from 'src/internal/routeToUrl';

import type {Route, RouteNames, RouteParameter} from 'src/types';

const FALLBACK_PATH = '/';

const emittedRouteMetrics: Record<string, boolean> = {};

export default function useRouteToUrl(): <T extends RouteNames>(
  route: RouteParameter<T> | Route,
  base?: Required<Route> | null,
) => string {
  const config = useNavigationConfig();

  const {increment} = useMetrics();
  const {warning} = useReports();

  return useCallback(
    <T extends RouteNames>(
      route: RouteParameter<T> | Route,
      base?: Required<Route> | null,
    ) => {
      try {
        return routeToUrl(config, route, base);
      } catch (error) {
        if (getEnvironment() !== 'production') {
          throw error;
        }

        warning(error, {
          tags: {
            metric: 'stripelets.navigation.cannot_generate_route',
          },
          extras: {
            route,
            base,
          },
        });

        if (!emittedRouteMetrics[route.name]) {
          emittedRouteMetrics[route.name] = true;
          increment('stripelets.navigation.cannot_generate_route');
        }

        return FALLBACK_PATH;
      }
    },
    [config, increment, warning],
  );
}
